import { memo, useMemo } from "react";
import { bool, string, number, node, shape, oneOfType, oneOf, func, object } from "prop-types";
import { STICKY_RIGHT_CLASS } from "@utils";
import { CellInput } from "@tools/EstimateEditor/components/Tables/cells/components/CellInput";
import { ValueDivider } from "@components";
import Grid from "@material-ui/core/Grid";
import useStyle from "./ValueCell.style";
import useCellStyle from "../../../../styles/CommonTableCellStyles.style";
import classnames from "classnames";

export const ValueCell = memo(({
  colName,
  value,
  valueMax,
  onValueChange,
  onMouseEnter,
  onMouseLeave,
  
  editable,
  emptyInputs,
  displayContent,
  
  placeholderMin,
  placeholderMax,
  inputMinTooltipMessage,
  inputMaxTooltipMessage,
  
  min = 0,
  max = 9999,
  
  children,
  useSeparator,
  highlightCellText,
  className,
  innerClassName,
  style,
  isHeader,
  useDynamicWidth,
  fullWidthInputs,
  focusAction,
  extendPadding,
  alignItems,
  displayMax,
  addInnerMargin,
  usePriceFormat,
  useInputBackground,
  textPresetClass,
  isSticky,
  allowOverflowDisplay,
  navigationKey,
  cellContentDirection="row",
  justifyCellContent="center",
  visible = true,
  size="small",
  displayMinMaxSeparator = true,
  ...valueProps
}) => {
  const classes = useStyle({ colName, extendPadding });
  const cellClasses = useCellStyle();
  
  const dw = useDynamicWidth && colName;
  
  const editableMin = useMemo(() => Boolean(
    typeof editable === "boolean"
      ? editable || valueProps.messageContent || focusAction
      : editable?.min
  ), [ editable, valueProps.messageContent, focusAction ]);
  
  const navKeyMin = useMemo(() => (
    navigationKey && (displayMax ? navigationKey + "min" : navigationKey)
  ), [ navigationKey, displayMax ]);
  
  const editableMax = useMemo(() => Boolean(
    typeof editable === "boolean"
      ? editable || valueProps.messageContent || focusAction
      : editable?.max
  ), [ editable, valueProps.messageContent, focusAction ]);
  
  const navKeyMax = useMemo(() => (
    navigationKey && (displayMax ? navigationKey + "max" : navigationKey)
  ), [ navigationKey, displayMax ]);
  
  const handleValueChange = (isMax) => (value) => (
    onValueChange && onValueChange(value, isMax)
  )
  
  return (
    <Grid
      item
      container
      role="rowitem"
      className={classnames(
        "cell",
        cellClasses.cell,
        classes.valueCell,
        isHeader && cellClasses.headCell,
        useSeparator && cellClasses.withSeparator,
        // !dw && mini && classes.cellMini,
        // !dw && narrow && classes.cellSmall,
        // !dw && wider && classes.cellMedium,
        // !dw && widest && classes.cellLarge,
        dw && classes.dynamicWidth,
        !dw && classes[`cell-${size}`],
        // cellClasses[`size${capitalize(inputSize)}`],
        isSticky && colName && classes.isSticky,
        isSticky && colName && STICKY_RIGHT_CLASS,
        allowOverflowDisplay && "display-overflow-right",
        
        highlightCellText && "highlightCellText",
        !visible && cellClasses.cellOff,
        extendPadding && classes.extendPadding,
        className
      )}
      alignItems={alignItems || (isHeader ? "flex-end" : "flex-start")}
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Grid
        item container
        direction={cellContentDirection}
        justifyContent={justifyCellContent}
        alignItems="center"
        wrap="nowrap"
        className={classnames(
          (isHeader || useSeparator) && "relative",
          !isHeader && cellClasses.cellInner, innerClassName
        )}
      >
        {
          displayContent &&
          <Grid
            item container
            wrap="nowrap"
            alignItems="center"
            justifyContent="center"
            className={classnames(
              usePriceFormat ? "h-24 font-bold w-full" : "h-32",
              addInnerMargin && "mt-6",
              textPresetClass,
              isHeader && "preset-tableHeader",
              useInputBackground && classes.inputContainerBackground,
            )}
          >
            <CellInput
              editable={editableMin}
              emptyInputs={emptyInputs}
              usePriceFormat={usePriceFormat}
              value={value}
              valueType={displayMax ? "min" : "avg"}
              onValueChange={handleValueChange(false)}
              onFocus={focusAction}
              alignContent={displayMax ? "right" : "center"}
              min={typeof min === "number" ? min : min?.min}
              max={typeof max === "number" ? max : max?.min}
              placeholder={placeholderMin}
              tooltipMessage={inputMinTooltipMessage}
              fullWidthInputs={fullWidthInputs || displayMax || useInputBackground}
              applyReadonlyPadding={!editableMin && editableMax}
              size={usePriceFormat ? "small" : "large"}
              halfWidth={displayMax}
              navigationKey={navKeyMin}
              {...valueProps}
            />
            {
              displayMax && (!emptyInputs || editableMin || editableMax) &&
              <ValueDivider display={displayMinMaxSeparator} />
            }
            {
              displayMax &&
              <CellInput
                editable={editableMax}
                emptyInputs={emptyInputs}
                usePriceFormat={usePriceFormat}
                value={valueMax}
                valueType="max"
                onValueChange={handleValueChange(true)}
                onFocus={focusAction}
                alignContent="left"
                min={typeof min === "number" ? min : min?.max}
                max={typeof max === "number" ? max : max?.max}
                placeholder={placeholderMax}
                tooltipMessage={inputMaxTooltipMessage}
                fullWidthInputs={fullWidthInputs || displayMax || useInputBackground}
                applyReadonlyPadding={!editableMax && editableMin}
                size={usePriceFormat ? "small" : "large"}
                halfWidth={displayMax}
                navigationKey={navKeyMax}
                {...valueProps}
              />
            }
            {/*{*/}
            {/*  editable || messageContent || focusAction*/}
            {/*    ? <ValueCellEditableContent*/}
            {/*      {...valueProps}*/}
            {/*      messageContent={messageContent}*/}
            {/*      onValueChange={onValueChange}*/}
            {/*      className={textPresetClass}*/}
            {/*      symbolStart={modifiedSymbolStart}*/}
            {/*      onFocus={focusAction}*/}
            {/*      fullWidth={fullWidthInputs}*/}
            {/*      emptyInputs={emptyInputs}*/}
            {/*    />*/}
            {/*    : !emptyInputs && <ValueCellReadOnlyContent*/}
            {/*      showValueSign={showValueSign}*/}
            {/*      symbolStart={modifiedSymbolStart}*/}
            {/*      {...valueProps}*/}
            {/*    />*/}
            {/*}*/}
          </Grid>
        }
        { children }
        { useSeparator && <span className="separator" /> }
      </Grid>
    </Grid>
  );
});

ValueCell.propTypes = {
  colName: string,
  value: oneOfType([number, string]),
  valueMax: oneOfType([number, string]),
  onValueChange: func,
  displayContent: bool,
  visible: bool,
  displayMax: bool,
  editable: oneOfType([bool, shape({ min: bool, max: bool })]),
  min: oneOfType([number, shape({ min: number, max: number })]),
  max: oneOfType([number, shape({ min: number, max: number })]),
  children: oneOfType([node, string]), //reset button
  symbolStart: string,
  symbolEnd: string,
  textPresetClass: string,
  
  useSeparator: bool,
  // common
  highlightCellText: bool,
  showDecimals: bool,
  decimals: number,
  isHeader: bool,
  justifyCellContent: oneOf(["center", "flex-start", "flex-end"]),
  alignItems: oneOf(["center", "flex-start", "flex-end"]),
  cellContentDirection: oneOf(["row", "row-reverse", "column", "column-reverse"]),
  size: oneOf(["small", "medium", "large", "xLarge", "2xLarge"]),
  inputSize: oneOf([ "small", "large"]),
  onMouseEnter: func,
  onMouseLeave: func,
  showValueSign: bool,
  emptyInputs: bool,
  extendPadding: bool,
  innerClassName: string,
  inputClassName: string,
  inputMinTooltipMessage: oneOfType([string, node]),
  inputMaxTooltipMessage: oneOfType([string, node]),
  placeholderMin: oneOfType([number, string]),
  placeholderMax: oneOfType([number, string]),
  americanFormat: bool,
  useDynamicWidth: bool,
  isSticky: bool,
  fullWidthInputs: bool,
  applySuffixSpacing: bool,
  displayMinDialogMessage: bool,
  displayMaxDialogMessage: bool,
  displayMinMaxSeparator: bool,
  
  messageContent: string,
  messageTitle: string,
  focusAction: func,
  className: string,
  style: object,
  addInnerMargin: bool,
  usePriceFormat: bool,
  useInputBackground: bool,
  allowOverflowDisplay: bool,
};
